import React, { useState, useEffect, useRef, useMemo } from 'react';
import { classNames } from 'primereact/utils';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { RadioButton } from 'primereact/radiobutton';
import { AutoComplete } from 'primereact/autocomplete';
import { useFormik } from 'formik';
import { Toast } from 'primereact/toast';
import { translatedMessage } from '../../service/LanguageService';
import { EvaluationService } from '../../service/EvaluationService';
import ChooseMemberDialog from './ChooseMemberDialog';
import CompetitionUtils from '../../utilities/CompetitionUtils';
import { InputText } from 'primereact/inputtext';

const emptyMember = {
    id: null,
    user: {
        id: null,
        username: '',
    },
    evaluationCommitteeId: null,
    isPresident: false,
    isSecretary: false,
    isSimpleMember: false
};

const CommitteeMemberEditDialog = (props) => {
    const [member, setMember] = useState(emptyMember);
    const [isNew, setIsNew] = useState(false);
    const [allowSecretary, setAllowSecretary] = useState(true);
    const [isUpdateSecretary, setIsUpdateSecretary] = useState(true);

    const toast = useRef(null);

    const evaluationService = useMemo(() => new EvaluationService(), []);

    useEffect(() => {
        let _member = { ...emptyMember };
        setIsNew(true);
        if (props.value.id) {
            _member = { ...props.value };
            setIsNew(false);
        }
        _member.evaluationCommitteeId = { ...props.committeeId };
        setNameAndUsername(_member.user);
        setMember(_member);
        setIsUpdateSecretary(_member.isSecretary)
        setAllowSecretary(props.allowSecretary)
    }, [props, props.value]);

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: member,
        validate: (data) => {
            let errors = {};

            // console.log('formik validate', data);
            if (data.user && !data.user.id) {
                errors.user = translatedMessage('form.error.user.required');
            }

            return errors;
        },
        onSubmit: (data) => {
            saveMember(data);
            hideMemberDialog();
            // setMember(props.emptyMember);
            formik.resetForm();
        }
    });

    const isFormFieldValid = (name) => !!(formik.touched[name] && formik.errors[name]);
    const getFormErrorMessage = (name) => {
        return isFormFieldValid(name) && <small className="p-error text-align-left">{formik.errors[name]}</small>;
    };

    const handleRoleChange = (event) => {
        if (event.value === 'President') {
            formik.setFieldValue('isPresident', true);
            formik.setFieldValue('isSecretary', false);
        }
        if (event.value === 'Secretary') {
            formik.setFieldValue('isPresident', false);
            formik.setFieldValue('isSecretary', true);
        }
        if (event.value === 'SimpleMember') {
            formik.setFieldValue('isPresident', false);
            formik.setFieldValue('isSecretary', false);
        }
    }

    const hideMemberDialog = () => {
        formik.resetForm();
        if (typeof props.onCancel === 'function') {
            props.onCancel();
        }
    };

    const saveMember = (data) => {
        let _member = { ...data };
        // console.log('saveMember', _member);
        _member.evaluationCommitteeId = props.committeeId;
        evaluationService.saveCommitteeMember(_member)
            .then((savedMember) => {
                if (typeof props.afterSave === 'function') {
                    props.afterSave(savedMember, isNew, true, null);
                }                
            })
            .catch((error) => {
                if (typeof props.afterSave === 'function') {
                    props.afterSave(null, isNew, false, error);
                }
            });
    };

    const searchUser = () => {
        // console.log('searchUser', formik.values.user.username, props.committeeId);
        if (formik.values.user?.username && formik.values.user.username.trim() !== '') {
            evaluationService.searchPossibleMember(props.committeeId, formik.values.user.username)
                .then((_member) => {
                    if (_member) {
                        setChoosenMember(_member);
                    } else {
                        toast?.current?.show({ severity: 'warn', summary: translatedMessage("error.user.not_found"), life: 5000 });
                    }
                    return _member;
                })
                .catch((error) => {
                    toast?.current?.show({ severity: 'error', summary: translatedMessage(error), life: 5000 });
                });
        }
    };

    const handleSearchEnterKey = (e) => {
        // console.log('handleSearchEnterKey', member);
        if (member?.user?.id) {
            let _member = { ...emptyMember };
            setMember({ ..._member, id: member.id });
            formik.setFieldValue("user", _member.user)
        }
        if (e.key === 'Enter' || e.keyCode === 13) {
            e.preventDefault()
            searchUser()
        }
    }

    const handleUserSearch = () => {
        if (formik.values.user?.username) {
            searchUser()
        }
    }

    const setChoosenMember = (_member) => {
        setMember({ ..._member, id: member.id });
        setNameAndUsername(_member.user);
        formik.setFieldValue("user", _member.user)
        formik.setFieldValue("isPresident", false)
        formik.setFieldValue("isSecretary", false)
        formik.setFieldValue("isSimpleMember", true)
        setAllowSecretary(_member.canBeSecretary && (!props.hasSecretary || (props.hasSecretary && isUpdateSecretary)))
    }

    const setNameAndUsername = (user) => {
        user.id ? (user.nameAndUsername = user.fullName + ' (' + user.username + ')') : (user.nameAndUsername = '');
    };

    const committeeDialogFooter = (
        <>
            <Button label={translatedMessage('generic.cancel')} icon="pi pi-times" className="p-button-text" onClick={hideMemberDialog} />
            <Button label={translatedMessage('generic.save')} icon="pi pi-check" className="p-button-text" form="member-form" type="submit" />
        </>
    );

    return (
        <>
            <Toast ref={toast} />
            <Dialog
                visible={props.visible}
                style={{ width: '450px' }}
                header={translatedMessage('committee.member.details')}
                modal
                className="p-fluid"
                footer={committeeDialogFooter}
                onHide={hideMemberDialog}
            >
                <form id="member-form" onSubmit={formik.handleSubmit}>
                    <div className="p-inputgroup">
                        <span className="p-float-label">
                            {!CompetitionUtils.isJobCompetition(props?.competitionType) &&
                                <>
                                    <AutoComplete
                                        id="user"
                                        name="user"
                                        value={formik.values && formik.values.user}
                                        suggestions={null}
                                        readOnly={true}
                                        field="nameAndUsername"
                                        onChange={formik.handleChange}
                                        aria-label="Members"
                                        minLength={3}
                                        maxLength={20}
                                    />

                                    <ChooseMemberDialog onChooseMember={setChoosenMember} committeeId={props.committeeId} />

                                    <label htmlFor="user" className={classNames({ 'p-error': isFormFieldValid('user') })}>
                                        {translatedMessage('generic.name')} *
                                    </label>
                                </>
                            }
                            {CompetitionUtils.isJobCompetition(props?.competitionType) && <>
                                <InputText
                                    id="user"
                                    type="text"
                                    value={formik.values && (formik.values.user?.id ? formik.values.user.nameAndUsername : formik.values.user.username)}
                                    onChange={(e) => formik.setFieldValue("user", { ...member.user, username: e.target.value })}
                                    onKeyDown={handleSearchEnterKey}
                                    autoFocus={props.autoFocus}
                                />
                                <label htmlFor="user" className={classNames({ 'p-error': isFormFieldValid('user') })}>
                                    {translatedMessage("generic.seach.byEmail")} *
                                </label>

                                <Button
                                    type='button'
                                    icon={"pi pi-search"}
                                    className="p-button-rounded p-button-info ml-3"
                                    onClick={handleUserSearch}
                                    tooltip={translatedMessage('generic.seach.byEmail')}
                                    tooltipOptions={{ position: 'top' }}
                                    disabled={props?.disabled}
                                />
                            </>}
                        </span>
                    </div>
                    {getFormErrorMessage('user')}


                    <div className="mt-4 field-radiobutton">
                        <RadioButton inputId="isPresident" name="role" value="President" onChange={handleRoleChange} checked={formik.values && formik.values.isPresident} disabled={!props.allowPresident} />
                        <label htmlFor="isPresident">{translatedMessage('committee.member.isPresident')}</label>
                    </div>
                    <div className="field-radiobutton">
                        <RadioButton inputId="isSecretary" name="role" value="Secretary" onChange={handleRoleChange} checked={formik.values && formik.values.isSecretary} disabled={!allowSecretary} />
                        <label htmlFor="isSecretary">{translatedMessage('committee.member.isSecretary')}</label>
                    </div>
                    <div className="field-radiobutton">
                        <RadioButton inputId="isSimpleMember" name="role" value="SimpleMember" onChange={handleRoleChange} checked={formik.values && !formik.values.isPresident && !formik.values.isSecretary} />
                        <label htmlFor="isSimpleMember">{translatedMessage('committee.member.isSimpleMember')}</label>
                    </div>
                </form>
            </Dialog>
        </>
    );
};

export default CommitteeMemberEditDialog;
